/**
 * The shapes the panel SENDS, and the small predicates and formatters its
 * screens share. Pure, so the rules the backend's strict zod schemas enforce
 * (spec 2026-08-29 §6, mirrored from the services' pydantic models) are checked
 * here before a request leaves the browser, and a decimal is never turned into
 * a float on the way out.
 *
 * Nothing here adds an `operator` field: the backend injects it from the grant
 * and its `.strict()` schemas reject a client-sent one.
 */

export interface Problem {
  field: string;
  message: string;
}

export type FormResult<T> = { ok: true; body: T } | { ok: false; problems: Problem[] };

// ---------------------------------------------------------------------------
// Vocabularies — the services' enums, once each
// ---------------------------------------------------------------------------

export const THREAT_KINDS = ['leak', 'deepfake_wave', 'platform_incident', 'other'] as const;
export type ThreatKind = (typeof THREAT_KINDS)[number];

/** Queue priority order, highest first — also the order the depths render in. */
export const REVIEW_SEVERITIES = [
  'ncii_suspected',
  'explicit_unmatched',
  'unassessed',
  'benign_copy',
  'likely_not_subject',
] as const;
export type ReviewSeverity = (typeof REVIEW_SEVERITIES)[number];

export const REVIEW_DECISIONS = ['confirmed', 'rejected', 'uncertain'] as const;
export type ReviewDecision = (typeof REVIEW_DECISIONS)[number];

// ---------------------------------------------------------------------------
// Threat event
// ---------------------------------------------------------------------------

export interface ThreatEventForm {
  kind: string;
  title: string;
  body: string;
  severity: string;
  /** Free text: commas, spaces or newlines between domains. */
  domains: string;
  isGlobal: boolean;
  /** Decimal STRING — never parsed into a number for sending. */
  penalty: string;
  /** A `datetime-local` value, read as the browser's local time. */
  expiresAt: string;
  decayDays: string;
}

export interface ThreatEventBody {
  kind: ThreatKind;
  title: string;
  body: string;
  severity: number;
  domains: string[];
  is_global: boolean;
  penalty: string;
  expires_at: string;
  decay_days: number;
}

/** The services' NUMERIC(5,2): digits, optionally a point and up to two more. */
const PENALTY_RE = /^\d+(\.\d{1,2})?$/;

export function domainsList(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(/[\s,]+/)) {
    const domain = part.trim().toLowerCase();
    if (domain === '' || seen.has(domain)) continue;
    seen.add(domain);
    out.push(domain);
  }
  return out;
}

function integerBetween(raw: string, min: number, max: number): number | null {
  if (!/^-?\d+$/.test(raw.trim())) return null;
  const value = Number(raw);
  return value >= min && value <= max ? value : null;
}

export function threatEventBody(form: ThreatEventForm): FormResult<ThreatEventBody> {
  const problems: Problem[] = [];

  const kind = THREAT_KINDS.find((candidate) => candidate === form.kind);
  if (kind === undefined) problems.push({ field: 'kind', message: 'Pick a kind.' });

  const title = form.title.trim();
  if (title === '') problems.push({ field: 'title', message: 'A title is required.' });

  const severity = integerBetween(form.severity, 1, 5);
  if (severity === null) {
    problems.push({ field: 'severity', message: 'Severity is a whole number from 1 to 5.' });
  }

  const domains = form.isGlobal ? [] : domainsList(form.domains);
  if (!form.isGlobal && domains.length === 0) {
    problems.push({ field: 'domains', message: 'List at least one domain, or make the event global.' });
  }

  const penalty = form.penalty.trim();
  if (!PENALTY_RE.test(penalty) || Number(penalty) <= 0) {
    problems.push({
      field: 'penalty',
      message: 'Penalty is a decimal greater than 0 with up to two places, like 5.00.',
    });
  }

  const expiresMs = form.expiresAt.trim() === '' ? Number.NaN : new Date(form.expiresAt).getTime();
  if (Number.isNaN(expiresMs)) {
    problems.push({ field: 'expiresAt', message: 'Pick when the event expires.' });
  }

  const decayDays = integerBetween(form.decayDays, 1, Number.MAX_SAFE_INTEGER);
  if (decayDays === null) {
    problems.push({ field: 'decayDays', message: 'Decay is a whole number of days, at least 1.' });
  }

  if (problems.length > 0 || kind === undefined || severity === null || decayDays === null) {
    return { ok: false, problems };
  }
  return {
    ok: true,
    body: {
      kind,
      title,
      body: form.body.trim(),
      severity,
      domains,
      is_global: form.isGlobal,
      penalty,
      // ISO-8601 with an offset (`Z`): the backend's z.string().datetime({ offset: true }).
      expires_at: new Date(expiresMs).toISOString(),
      decay_days: decayDays,
    },
  };
}

// ---------------------------------------------------------------------------
// Article
// ---------------------------------------------------------------------------

export interface ArticleImageRow {
  url: string;
  alt: string;
}

export interface ArticleSourceRow {
  name: string;
  url: string;
}

export interface ArticleForm {
  title: string;
  summary: string;
  body: string;
  images: ArticleImageRow[];
  sources: ArticleSourceRow[];
}

export type ArticleBody = ArticleForm;

const ARTICLE_LIMITS = {
  title: 200,
  summary: 500,
  body: 20_000,
  alt: 300,
  sourceName: 120,
  rows: 10,
} as const;

export function isHttpsUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname !== '';
  } catch {
    return false;
  }
}

/**
 * A picture an operator uploaded through POST /v1/admin/articles/images, as
 * opposed to a web address someone pasted.
 *
 * The bucket's identity is the BACKEND's to check — it holds the config, and
 * its schema refuses anything else. This is the client-side half: enough to
 * say "that is not an uploaded picture" in words an operator can act on,
 * without the panel needing to know which bucket is ours.
 */
export function isUploadedArticleImage(value: string): boolean {
  return /^https:\/\/[a-z0-9.-]+\.s3\.[a-z0-9-]+\.amazonaws\.com\/article-images\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(
    value,
  );
}

export function articleBody(form: ArticleForm): FormResult<ArticleBody> {
  const problems: Problem[] = [];

  const title = form.title.trim();
  if (title.length < 1 || title.length > ARTICLE_LIMITS.title) {
    problems.push({ field: 'title', message: `A title of 1–${String(ARTICLE_LIMITS.title)} characters is required.` });
  }
  const summary = form.summary.trim();
  if (summary.length > ARTICLE_LIMITS.summary) {
    problems.push({ field: 'summary', message: `The summary is at most ${String(ARTICLE_LIMITS.summary)} characters.` });
  }
  const body = form.body.trim();
  if (body.length > ARTICLE_LIMITS.body) {
    problems.push({ field: 'body', message: `The body is at most ${String(ARTICLE_LIMITS.body)} characters.` });
  }

  // A row with nothing in it is a blank the editor left behind; a row with
  // SOMETHING in it is an intent, and a broken intent is refused, not dropped.
  const images: ArticleImageRow[] = [];
  form.images.forEach((row, index) => {
    const url = row.url.trim();
    const alt = row.alt.trim();
    if (url === '' && alt === '') return;
    if (!isUploadedArticleImage(url)) {
      problems.push({
        field: `images.${String(index)}.url`,
        // NAMED BY POSITION, deliberately. An article published before uploads
        // existed carries a pasted address; it keeps rendering in the app but
        // cannot be saved again. The operator hits this while adding a NEW
        // picture, so a message about "the picture" reads as their upload
        // having failed — and that row has no input left to highlight, so the
        // words are the only thing that can point at it.
        message: `Picture ${String(index + 1)} is a web address, not an uploaded picture. Remove it, then add the picture again.`,
      });
    }
    if (alt.length > ARTICLE_LIMITS.alt) {
      problems.push({ field: `images.${String(index)}.alt`, message: `Alt text is at most ${String(ARTICLE_LIMITS.alt)} characters.` });
    }
    images.push({ url, alt });
  });
  if (images.length > ARTICLE_LIMITS.rows) {
    problems.push({ field: 'images', message: `At most ${String(ARTICLE_LIMITS.rows)} pictures.` });
  }

  const sources: ArticleSourceRow[] = [];
  form.sources.forEach((row, index) => {
    const name = row.name.trim();
    const url = row.url.trim();
    if (name === '' && url === '') return;
    if (name.length < 1 || name.length > ARTICLE_LIMITS.sourceName) {
      problems.push({ field: `sources.${String(index)}.name`, message: `A source name of 1–${String(ARTICLE_LIMITS.sourceName)} characters is required.` });
    }
    if (!isHttpsUrl(url)) {
      problems.push({ field: `sources.${String(index)}.url`, message: 'Source URLs must be https:// with a host.' });
    }
    sources.push({ name, url });
  });
  if (sources.length > ARTICLE_LIMITS.rows) {
    problems.push({ field: 'sources', message: `At most ${String(ARTICLE_LIMITS.rows)} sources.` });
  }

  if (problems.length > 0) return { ok: false, problems };
  return { ok: true, body: { title, summary, body, images, sources } };
}

// ---------------------------------------------------------------------------
// Small predicates and formatters
// ---------------------------------------------------------------------------

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: string): boolean {
  return UUID_RE.test(value);
}

/** Money crosses as a decimal STRING; this only prefixes it. `null` is "no budget". */
export function fmtUsd(value: string | null): string {
  return value === null ? '—' : `$${value}`;
}

/** `null` means no calls in the window — NOT 0%, which would read as "always failing". */
export function fmtPct(rate: number | null): string {
  return rate === null ? '—' : `${String(Math.round(rate * 100))}%`;
}

export function fmtWhen(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return '—';
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : at.toLocaleString();
}

export function queueTotal(queue: Record<string, number>): number {
  return Object.values(queue).reduce((sum, count) => sum + count, 0);
}
